import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { UiIllustrationComponent } from '../illustration/ui-illustration.component';

/**
 * refresh-parent-portal-ui 0.1: full-width summary card (a child card). Tone
 * colours the card; `[avatar]` and `[status]` slots hold the initials circle
 * and the status pill.
 */
@Component({
  selector: 'ui-hero-card',
  standalone: true,
  imports: [UiIllustrationComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="hero">
      <ng-content select="[avatar]"></ng-content>
      <div class="main">
        <div class="title">{{ title() }}</div>
        @if (subtitle()) {
          <div class="sub">{{ subtitle() }}</div>
        }
      </div>
      <ng-content select="[end]"></ng-content>
      <ng-content select="[status]"></ng-content>
      @if (illustration()) {
        <ui-illustration [name]="illustration()" [size]="28" />
      }
    </div>
  `,
  styles: [
    `
      .hero {
        display: flex;
        gap: 14px;
        align-items: center;
        padding: 14px;
        border-radius: var(--gp-radius-card);
        border: 1.5px solid var(--gp-hc-border);
        background: var(--gp-hc-bg);
        color: var(--gp-hc-ink);
        margin-bottom: 10px;
        box-shadow: var(--gp-shadow);
      }
      .main {
        flex: 1;
        min-width: 0;
      }
      .title {
        font-size: 19px;
        font-weight: 900;
        margin-bottom: 4px;
      }
      .sub {
        font-size: 14px;
        font-weight: 600;
        color: var(--gp-ink-muted);
      }
      :host([data-tone='sage']) {
        --gp-hc-bg: var(--gp-tile-sage-bg);
        --gp-hc-border: var(--gp-tile-sage-border);
        --gp-hc-ink: var(--gp-tile-sage-ink);
      }
      :host([data-tone='mint']) {
        --gp-hc-bg: var(--gp-tile-mint-bg);
        --gp-hc-border: var(--gp-tile-mint-border);
        --gp-hc-ink: var(--gp-tile-mint-ink);
      }
      :host([data-tone='sky']) {
        --gp-hc-bg: var(--gp-tile-sky-bg);
        --gp-hc-border: var(--gp-tile-sky-border);
        --gp-hc-ink: var(--gp-tile-sky-ink);
      }
      :host([data-tone='butter']) {
        --gp-hc-bg: var(--gp-tile-butter-bg);
        --gp-hc-border: var(--gp-tile-butter-border);
        --gp-hc-ink: var(--gp-tile-butter-ink);
      }
      :host([data-tone='peach']) {
        --gp-hc-bg: var(--gp-tile-peach-bg);
        --gp-hc-border: var(--gp-tile-peach-border);
        --gp-hc-ink: var(--gp-tile-peach-ink);
      }
      :host([data-tone='lavender']) {
        --gp-hc-bg: var(--gp-tile-lavender-bg);
        --gp-hc-border: var(--gp-tile-lavender-border);
        --gp-hc-ink: var(--gp-tile-lavender-ink);
      }
    `,
  ],
  host: {
    '[attr.data-tone]': 'tone()',
  },
})
export class UiHeroCardComponent {
  readonly title = input.required<string>();
  readonly subtitle = input('');
  readonly illustration = input(''); // optional trailing illustration name
  readonly tone = input<'sage' | 'mint' | 'sky' | 'butter' | 'peach' | 'lavender'>('mint');
}